import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Calendar, CalendarSync, ChevronRight, Dumbbell, FileSignature, Loader2, LogOut, Phone, ShieldCheck, User, Pencil, Wallet, Repeat } from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { LoginRequiredEmptyState } from '@/components/LoginRequiredEmptyState'
import { useUserPreferences, usePreferenceModalities } from '@/hooks/useUserPreferences'
import { UserPreferencesModal } from '@/components/UserPreferencesModal'
import { MarketplaceHeader } from '@/components/MarketplaceHeader'
import { Button, ButtonVariant, Body } from '@meiuca/ui-components'
import { analyticsService } from '@/lib/firebase'
import { ANALYTICS_EVENTS, SCREEN_NAMES } from '@/constants/analyticsEvents'
import { appPreferencesManager } from '@/managers/appPreferencesManager'
import type { SportModality } from '@/types/address'

function getInitials(name?: string | null) {
  if (!name) return 'U'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return 'U'
  return parts
    .map(part => part[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export function ProfilePage() {
  const navigate = useNavigate()
  const { user, logout, isAuthenticated, isLoading: isAuthLoading } = useAuth()
  const { preferences, savePreferences } = useUserPreferences({
    autoLoad: true,
    isAuthenticated
  })

  const { modalities, isLoading: isLoadingModalities } = usePreferenceModalities(preferences?.modalities)
  const [isPreferencesModalOpen, setIsPreferencesModalOpen] = useState(false)
  const [isLoggingOut, setIsLoggingOut] = useState(false)
  const [analyticsConsent, setAnalyticsConsent] = useState(() => appPreferencesManager.getAnalyticsConsent())

  useEffect(() => {
    analyticsService.trackScreenView(SCREEN_NAMES.PROFILE)
  }, [])

  const handleToggleAnalyticsConsent = (enabled: boolean) => {
    if (enabled) {
      appPreferencesManager.setAnalyticsConsent(true)
      analyticsService.setTrackingEnabled(true)
      analyticsService.track(ANALYTICS_EVENTS.ANALYTICS_CONSENT_CHANGED, { enabled: true })
    } else {
      // Track the opt-out BEFORE disabling, so the event still dispatches.
      analyticsService.track(ANALYTICS_EVENTS.ANALYTICS_CONSENT_CHANGED, { enabled: false })
      appPreferencesManager.setAnalyticsConsent(false)
      analyticsService.setTrackingEnabled(false)
    }
    setAnalyticsConsent(enabled)
  }

  const handleShortcut = (shortcut: string, path: string) => {
    analyticsService.track(ANALYTICS_EVENTS.PROFILE_SHORTCUT_CLICKED, { shortcut })
    navigate(path)
  }

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true)
      analyticsService.track(ANALYTICS_EVENTS.USER_LOGGED_OUT, {})
      await logout()
      navigate('/')
    } catch (error) {
      console.error('Error logging out:', error)
      setIsLoggingOut(false)
    }
  }

  const handleOpenPreferencesModal = () => {
    setIsPreferencesModalOpen(true)
    analyticsService.track(ANALYTICS_EVENTS.PREFERENCES_MODAL_VIEWED, {
      source: 'profile_page'
    })
  }

  const handleSavePreferences = async (selectedModalities: SportModality[]) => {
    try {
      await savePreferences(selectedModalities)
      setIsPreferencesModalOpen(false)

      analyticsService.track(ANALYTICS_EVENTS.PREFERENCES_SAVED, {
        source: 'profile_page',
        modalities_count: selectedModalities.length,
        modalities: selectedModalities.map(m => m.id).join(',')
      })
    } catch (error) {
      console.error('Error saving preferences:', error)
      analyticsService.track(ANALYTICS_EVENTS.PREFERENCES_SAVE_ERROR, {
        source: 'profile_page',
        error_message: error instanceof Error ? error.message : 'Unknown error'
      })
    }
  }

  if (isAuthLoading) {
    return (
      <div className="min-h-screen bg-background-secondary">
        <MarketplaceHeader />
        <div className="max-w-2xl mx-auto px-4 py-8 flex justify-center">
          <Loader2 className="w-10 h-10 text-brand-primary animate-spin" />
        </div>
      </div>
    )
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-background-secondary">
        <MarketplaceHeader />
        <div className="max-w-2xl mx-auto px-4 py-8">
          <LoginRequiredEmptyState
            title="Entre na sua conta"
            description="Faça login para ver e editar seu perfil."
          />
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-background-secondary">
      <MarketplaceHeader />

      <div className="max-w-2xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-6"
        >
          {/* Profile Header — Avatar + Name */}
          <div className="flex flex-col items-center text-center pt-4 pb-2">
            <div className="w-20 h-20 rounded-full bg-brand-primary flex items-center justify-center mb-4 shadow-lg">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={user.name}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                <span className="text-white text-2xl font-bold">
                  {getInitials(user?.name)}
                </span>
              )}
            </div>
            <h1 className="text-xl font-bold text-text-primary">
              {user?.name || 'Usuário'}
            </h1>
            {user?.nickname && (
              <p className="text-sm text-text-secondary mt-0.5">@{user.nickname}</p>
            )}
          </div>

          {/* Personal Info Card */}
          <div className="bg-background-white rounded-2xl shadow-sm border border-border-light overflow-hidden">
            <div className="px-5 py-3 border-b border-border-light">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Informações pessoais
              </h2>
            </div>
            <div className="divide-y divide-border-light">
              <div className="flex items-center gap-3 px-5 py-4">
                <User className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-text-tertiary">Nome</p>
                  <p className="text-sm font-medium text-text-primary truncate">
                    {user?.name || 'Não informado'}
                  </p>
                </div>
              </div>
              {user?.nickname && (
                <div className="flex items-center gap-3 px-5 py-4">
                  <User className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-text-tertiary">Apelido</p>
                    <p className="text-sm font-medium text-text-primary truncate">
                      {user.nickname}
                    </p>
                  </div>
                </div>
              )}
              {user?.phoneNumber && (
                <div className="flex items-center gap-3 px-5 py-4">
                  <Phone className="w-4 h-4 text-text-tertiary flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-text-tertiary">Telefone</p>
                    <p className="text-sm font-medium text-text-primary truncate">
                      {user.phoneNumber}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Sport Preferences Card */}
          <div className="bg-background-white rounded-2xl shadow-sm border border-border-light overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3 border-b border-border-light">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Esportes favoritos
              </h2>
              <button
                onClick={handleOpenPreferencesModal}
                className="flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary/80 transition-colors"
              >
                <Pencil className="w-3 h-3" />
                Editar
              </button>
            </div>
            <div className="px-5 py-4">
              {isLoadingModalities ? (
                <div className="flex items-center gap-2 text-text-secondary text-sm py-2">
                  <div className="w-4 h-4 border-2 border-border-light border-t-brand-primary rounded-full animate-spin" />
                  Carregando...
                </div>
              ) : modalities && modalities.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {modalities.map((modality) => (
                    <span
                      key={modality.id}
                      className="inline-flex items-center px-3 py-1.5 rounded-full bg-brand-primary/10 text-brand-primary text-sm font-medium"
                    >
                      {modality.name}
                    </span>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Body className="text-text-secondary text-sm mb-3">
                    Nenhum esporte selecionado
                  </Body>
                  <Button
                    onClick={handleOpenPreferencesModal}
                    variant={ButtonVariant.PRIMARY}
                  >
                    Selecionar Esportes
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Treino recorrente no CT e reserva avulsa na arena são jornadas
              distintas — numa lista só o usuário das duas se perde. */}
          <div className="bg-background-white rounded-2xl shadow-sm border border-border-light overflow-hidden">
            <div className="px-5 py-3 border-b border-border-light">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Centros de treinamento
              </h2>
            </div>
            <div className="divide-y divide-border-light">
              <button
                onClick={() => handleShortcut('plans', '/planos')}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-background-secondary transition-colors"
              >
                <Wallet className="w-4 h-4 text-brand-primary flex-shrink-0" />
                <span className="flex-1 text-sm font-medium text-text-primary text-left">
                  Meus planos
                </span>
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              </button>
              <button
                onClick={() => handleShortcut('workouts', '/treinos')}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-background-secondary transition-colors"
              >
                <Dumbbell className="w-4 h-4 text-brand-primary flex-shrink-0" />
                <span className="flex-1 text-sm font-medium text-text-primary text-left">
                  Meus treinos
                </span>
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              </button>
              <button
                onClick={() => handleShortcut('makeups', '/reposicoes')}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-background-secondary transition-colors"
              >
                <CalendarSync className="w-4 h-4 text-brand-primary flex-shrink-0" />
                <span className="flex-1 text-sm font-medium text-text-primary text-left">
                  Reposições
                </span>
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              </button>
              <button
                onClick={() => handleShortcut('signed_terms', '/termos-assinados')}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-background-secondary transition-colors"
              >
                <FileSignature className="w-4 h-4 text-brand-primary flex-shrink-0" />
                <span className="flex-1 text-sm font-medium text-text-primary text-left">
                  Termos assinados
                </span>
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              </button>
            </div>
          </div>

          <div className="bg-background-white rounded-2xl shadow-sm border border-border-light overflow-hidden">
            <div className="px-5 py-3 border-b border-border-light">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Arenas
              </h2>
            </div>
            <div className="divide-y divide-border-light">
              <button
                onClick={() => handleShortcut('reservations', '/reservations')}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-background-secondary transition-colors"
              >
                <Calendar className="w-4 h-4 text-brand-primary flex-shrink-0" />
                <span className="flex-1 text-sm font-medium text-text-primary text-left">
                  Minhas reservas
                </span>
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              </button>
              <button
                onClick={() => handleShortcut('memberships', '/mensalistas')}
                className="w-full flex items-center gap-3 px-5 py-4 hover:bg-background-secondary transition-colors"
              >
                <Repeat className="w-4 h-4 text-brand-primary flex-shrink-0" />
                <span className="flex-1 text-sm font-medium text-text-primary text-left">
                  Meus horários fixos
                </span>
                <ChevronRight className="w-4 h-4 text-text-tertiary" />
              </button>
            </div>
          </div>

          {/* Privacy / Analytics */}
          <div className="bg-background-white rounded-2xl shadow-sm border border-border-light overflow-hidden">
            <div className="px-5 py-3 border-b border-border-light">
              <h2 className="text-sm font-semibold text-text-secondary uppercase tracking-wide">
                Privacidade
              </h2>
            </div>
            <div className="flex items-center gap-3 px-5 py-4">
              <ShieldCheck className="w-4 h-4 text-brand-primary flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-text-primary">Analytics de uso</p>
                <p className="text-xs text-text-tertiary">
                  Ajuda a melhorar sua experiência. Nunca coletamos dados pessoais.
                </p>
              </div>
              <button
                type="button"
                role="switch"
                aria-checked={analyticsConsent}
                aria-label="Permitir analytics de uso"
                onClick={() => handleToggleAnalyticsConsent(!analyticsConsent)}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors ${
                  analyticsConsent ? 'bg-brand-primary' : 'bg-border-light'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                    analyticsConsent ? 'translate-x-5' : 'translate-x-0.5'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Logout */}
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="w-full flex items-center justify-center gap-2 px-5 py-3 rounded-2xl border border-border-light text-feedback-error hover:bg-feedback-error/5 transition-colors disabled:opacity-50"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm font-medium">
              {isLoggingOut ? 'Saindo...' : 'Sair da conta'}
            </span>
          </button>
        </motion.div>
      </div>

      {/* Preferences Modal */}
      <UserPreferencesModal
        isOpen={isPreferencesModalOpen}
        onClose={() => setIsPreferencesModalOpen(false)}
        onSave={handleSavePreferences}
        allowSkip={false}
        initialSelectedIds={preferences?.modalities}
      />
    </div>
  )
}
